import React, { useState, useEffect } from 'react';
import type { QuizFormProps, Question, Quiz } from '../../types';
import { Button } from '../ui/button';
import QuestionForm from './QuestionForm';
import JSONImporter from './JSONImporter';

const defaultQuestion: Question = {
  text: '',
  timeLimit: 30,
  pointValue: 100,
  options: [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
};

const QuizForm: React.FC<QuizFormProps> = ({
  initialQuiz,
  onSubmit,
  isEditing = false,
}) => {
  const [quiz, setQuiz] = useState<Quiz>(
    initialQuiz || {
      title: '',
      description: '',
      questions: [{ ...defaultQuestion }],
    }
  );

  const [errors, setErrors] = useState<{
    title?: string;
    questions?: string;
  }>({});

  useEffect(() => {
    if (initialQuiz) {
      setQuiz(initialQuiz);
    }
  }, [initialQuiz]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuiz({ ...quiz, title: e.target.value });
    if (errors.title) {
      setErrors({ ...errors, title: undefined });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuiz({ ...quiz, description: e.target.value });
  };

  const handleQuestionChange = (index: number, updatedQuestion: Question) => {
    const updatedQuestions = [...quiz.questions];
    updatedQuestions[index] = updatedQuestion;
    setQuiz({ ...quiz, questions: updatedQuestions });
    
    if (errors.questions) {
      setErrors({ ...errors, questions: undefined });
    }
  };

  const handleDeleteQuestion = (index: number) => {
    if (quiz.questions.length > 1) {
      const updatedQuestions = quiz.questions.filter((_, i) => i !== index);
      setQuiz({ ...quiz, questions: updatedQuestions });
    }
  };

  const handleAddQuestion = () => {
    setQuiz({
      ...quiz,
      questions: [...quiz.questions, { ...defaultQuestion }],
    });
  };

  const handleImport = (importedQuiz: Quiz) => {
    setQuiz(importedQuiz);
  };

  const validateQuiz = (): boolean => {
    const newErrors: { title?: string; questions?: string } = {};
    let isValid = true;

    if (!quiz.title.trim()) {
      newErrors.title = 'Title is required';
      isValid = false;
    }

    // Validate questions
    for (const question of quiz.questions) {
      if (!question.text.trim()) {
        newErrors.questions = 'All questions must have text';
        isValid = false;
        break;
      }

      // Check all options have text
      for (const option of question.options) {
        if (!option.text.trim()) {
          newErrors.questions = 'All answer options must have text';
          isValid = false;
          break;
        }
      }

      // Ensure at least one option is correct
      if (!question.options.some(option => option.isCorrect)) {
        newErrors.questions = 'Each question must have at least one correct answer';
        isValid = false;
        break;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateQuiz()) {
      onSubmit(quiz);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-lg font-heading font-bold mb-2">
            Quiz Title
          </label>
          <input
            type="text"
            className={`w-full h-12 px-4 border-2 rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
              errors.title ? 'border-destructive' : 'border-border'
            }`}
            value={quiz.title}
            onChange={handleTitleChange}
            placeholder="Enter quiz title"
          />
          {errors.title && (
            <p className="text-destructive text-sm font-medium mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block text-lg font-heading font-bold mb-2">
            Description
          </label>
          <textarea
            className="w-full px-4 py-3 border-2 border-border rounded-lg min-h-20 bg-input focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            value={quiz.description}
            onChange={handleDescriptionChange}
            placeholder="Enter quiz description (optional)"
          />
        </div>

        <JSONImporter onImport={handleImport} />

        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-heading font-bold">Questions</h3>
            <Button 
              type="button" 
              onClick={handleAddQuestion}
              variant="outline"
            >
              Add Question
            </Button>
          </div>

          {errors.questions && (
            <p className="text-destructive text-sm font-medium bg-destructive/10 p-3 rounded-lg mb-4">{errors.questions}</p>
          )}

          {quiz.questions.map((question, index) => (
            <QuestionForm
              key={index}
              question={question}
              onChange={(updatedQuestion) => handleQuestionChange(index, updatedQuestion)}
              onDelete={() => handleDeleteQuestion(index)}
            />
          ))}
        </div>

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button type="submit">
            {isEditing ? 'Save Quiz' : 'Create Quiz'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuizForm;